import { NeighborhoodServiceAreaInfo } from 'rwl-shared/dist/models';
import React from 'react';
import styled from '@emotion/styled';
import { Body1, Body2, Subtitle2 } from 'rwl-ui';
import { useGlobal } from '../../../hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getIcon } from '../../../utils';
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';

const Container = styled.div`
    width: 164px;
    max-height: 200px;
    border-radius: 8px;
    display: inline-flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
`;

const Content = styled.div`
    width: 100%;
    padding: var(--rwl-spacing-md);
    background: white;
    box-shadow: 0px 2px 12px rgba(0, 0, 0, 0.2);
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    overflow: auto;
    scrollbar-width: none;
`;

const TitleSection = styled(Subtitle2)`
    padding: var(--rwl-spacing-xs);
    font-weight: 600;
`;

const Option = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--rwl-background-level2);
    padding: var(--rwl-spacing-xs);
    cursor: pointer;
    &:hover {
        background-color: var(--rwl-background-level1);
    }
`;

const ZipCode = styled(Body1)`
    font-weight: 500;
`;

const JobCountContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    width: 40%;
    justify-content: space-between;
`;

const JobCount = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    width: 55%;
    justify-content: space-between;
`;

const JobsNumber = styled(Body2)`
    color: var(--rwl-text-default);
`;

type SingleZipPopoverProps = {
    serviceAreas: NeighborhoodServiceAreaInfo[];
    onClick?: (area: NeighborhoodServiceAreaInfo) => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

const MultiZipPopover: React.FC<SingleZipPopoverProps> = ({
    serviceAreas,
    onClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    const { config } = useGlobal();
    const icon = config?.account.mapSettings.icon;
    const iconTint = config?.account.mapSettings.color;

    const sortByZipCode = (
        a: NeighborhoodServiceAreaInfo,
        b: NeighborhoodServiceAreaInfo
    ) => {
        const zipA = parseInt(a.postcode);
        const zipB = parseInt(b.postcode);
        // If both are valid numbers, compare numerically
        if (!isNaN(zipA) && !isNaN(zipB)) {
            return zipA - zipB;
        }
        // Fallback to string comparison if parsing fails
        return (a.postcode || '').localeCompare(b.postcode || '');
    };

    return (
        <Container onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
            <Content>
                <TitleSection>See projects in...</TitleSection>
                {serviceAreas.sort(sortByZipCode).map((serviceArea) => (
                    <Option
                        onClick={
                            onClick ? () => onClick(serviceArea) : undefined
                        }
                        key={serviceArea.postcode}
                    >
                        <ZipCode>{serviceArea.postcode}</ZipCode>
                        <JobCountContainer>
                            <JobCount>
                                <FontAwesomeIcon
                                    size="1x"
                                    icon={getIcon(icon)}
                                    color={iconTint}
                                    style={{
                                        marginRight: 'var(--rwl-spacing-xs)',
                                    }}
                                />
                                <JobsNumber>{serviceArea.jobCount}</JobsNumber>
                            </JobCount>
                            <FontAwesomeIcon size="xs" icon={faChevronRight} />
                        </JobCountContainer>
                    </Option>
                ))}
            </Content>
        </Container>
    );
};

export default MultiZipPopover;
